/**
 * HistoryManager
 * Custom history stack management for reliable back navigation restoration
 *
 * Strategy:
 * - Push to stack on page load (fresh visit)
 * - Update last entry on scroll settle (all pages)
 * - Pop before navigating on back button
 * - Restore scroll position on back navigation (all pages)
 */
class HistoryManager {
  // Use centralized config (with fallbacks)
  static get BACK_TRIGGER_STATE() {
    return typeof HistoryConfig !== "undefined"
      ? HistoryConfig.BACK_TRIGGER_STATE
      : "infiniteScroll:backTrigger";
  }
  static get SCROLL_SETTLE_DELAY() {
    return typeof HistoryConfig !== "undefined"
      ? HistoryConfig.SCROLL_SETTLE_DELAY
      : 150;
  }
  static get MAX_STACK_SIZE() {
    return typeof HistoryConfig !== "undefined"
      ? HistoryConfig.MAX_STACK_SIZE
      : 20;
  }

  static isInitialized = false;
  static navigationInProgress = false;
  static scrollUpdateTimeout = null;

  /**
   * Initialize history management on page load
   * Should be called on ALL pages for consistent back navigation
   */
  static initialize() {
    if (this.isInitialized) return;

    this.setupPopstateListener();
    this.isInitialized = true;
  }

  /**
   * Setup the double-state pattern for reliable back detection
   * State stack: ['backTrigger'] ← [{}] (current)
   * @param {boolean} skipPushState - If true, only replace state (for reloads to avoid duplicate history entries)
   */
  static setupHistoryState(skipPushState = false) {
    history.replaceState(this.BACK_TRIGGER_STATE, "", window.location.href);
    if (!skipPushState) {
      history.pushState({}, "", window.location.href);
    }
  }

  /**
   * Setup popstate listener for back navigation detection
   */
  static setupPopstateListener() {
    window.addEventListener("popstate", (event) => {
      if (event.state === this.BACK_TRIGGER_STATE) {
        this.handleBackNavigation();
      }
    });
  }

  /**
   * Setup scroll listener for non-collection pages
   * Collection pages use InfiniteScrollController's scroll handling
   */
  static setupScrollListener() {
    window.addEventListener(
      "scroll",
      () => {
        this.handlePageScroll();
      },
      { passive: true }
    );
  }

  /**
   * Handle page scroll with debounce
   * Called on non-collection pages
   */
  static handlePageScroll() {
    if (this.scrollUpdateTimeout) {
      clearTimeout(this.scrollUpdateTimeout);
    }

    this.scrollUpdateTimeout = setTimeout(() => {
      this.updateCurrentState();
    }, this.SCROLL_SETTLE_DELAY);
  }

  /**
   * Push current page to history stack
   * Called on fresh page load (not restoration)
   * Skips if the last entry has the same URL (prevents duplicates on reload)
   * @returns {boolean} Returns false if skipped (same URL), true if pushed
   */
  static pushCurrentPage() {
    const stack = CacheManager.getHistoryStack();
    const currentUrl = window.location.href;

    // Check if the last entry has the same URL (reload scenario)
    if (stack.length > 0) {
      const lastEntry = stack[stack.length - 1];
      if (lastEntry.url === currentUrl) {
        return false;
      }
    }

    const entry = {
      url: currentUrl,
      restore: {
        pageScrollY: window.scrollY,
        grids: {}, // Per-grid state will be populated on scroll
      },
      timestamp: Date.now(),
    };

    if (stack.length >= this.MAX_STACK_SIZE) {
      stack.shift();
    }

    stack.push(entry);
    CacheManager.setHistoryStack(stack);
    return true;
  }

  /**
   * Update the last entry in stack with current scroll state
   * Multi-grid version: accepts grid states object and page scroll Y
   *
   * @param {Object} gridStates - Object with gridId as keys: { gridId: { carouselIndex, activeFeed, height, loadedCount, totalCount, products } }
   * @param {number} [pageScrollY] - Page scroll Y position (defaults to window.scrollY)
   */
  static updateCurrentState(gridStates = {}, pageScrollY = null) {
    const stack = CacheManager.getHistoryStack();
    if (stack.length === 0) {
      return;
    }

    const lastEntry = stack[stack.length - 1];

    if (lastEntry.url !== window.location.href) {
      return;
    }

    // Initialize grids object if not present
    if (!lastEntry.restore.grids) {
      lastEntry.restore.grids = {};
    }

    // Always update pageScrollY
    lastEntry.restore.pageScrollY = pageScrollY !== null ? pageScrollY : window.scrollY;

    // Update grid states - include products and pagination state for self-contained history entries
    for (const [gridId, gridState] of Object.entries(gridStates)) {
      lastEntry.restore.grids[gridId] = {
        carouselIndex: gridState.carouselIndex !== undefined ? gridState.carouselIndex : 0,
        activeFeed: gridState.activeFeed || "collection",
        height: gridState.height !== undefined ? gridState.height : 0,
        loadedCount: gridState.loadedCount !== undefined ? gridState.loadedCount : 0,
        totalCount: gridState.totalCount !== undefined ? gridState.totalCount : 0,
        products: gridState.products || [],
        lastCursor: gridState.lastCursor || null,
        hasMore: gridState.hasMore || false,
        filters: gridState.filters || [],
      };
    }

    lastEntry.timestamp = Date.now();

    CacheManager.setHistoryStack(stack);
  }

  /**
   * Handle back navigation
   * Pop current entry, then navigate to previous
   */
  static handleBackNavigation() {
    if (this.navigationInProgress) {
      return;
    }

    const stack = CacheManager.getHistoryStack();

    if (stack.length > 0) {
      const popped = stack.pop();
      CacheManager.setHistoryStack(stack);
    }

    if (stack.length === 0) {
      if (history.length > 2) {
        history.pushState({}, "", window.location.href);
        history.go(1 - history.length);
        return;
      } else {
        history.back();
        return;
      }
    }

    this.navigationInProgress = true;
    CacheManager.setRestoreFlag(true);

    const previousEntry = stack[stack.length - 1];
    window.location.href = previousEntry.url;
  }

  /**
   * Check if current page load is a restoration (back navigation)
   * @returns {boolean}
   */
  static isRestoration() {
    return CacheManager.getRestoreFlag();
  }

  /**
   * Get restoration parameters if this is a back navigation
   * @returns {Object|null} Restoration params with grids object or null
   * Grid states include `products` array for self-contained restoration
   */
  static getRestorationParams() {
    if (!this.isRestoration()) return null;

    const stack = CacheManager.getHistoryStack();
    if (stack.length === 0) return null;

    const restore = stack[stack.length - 1].restore;

    // Ensure grids object exists
    if (!restore.grids) {
      restore.grids = {};
    }

    return restore;
  }

  /**
   * Restore scroll position for non-collection pages
   * Collection pages handle their own restoration via InfiniteScrollController
   */
  static restoreScrollPosition() {
    const params = this.getRestorationParams();
    if (!params) return;

    if (params.pageScrollY && params.pageScrollY > 0) {
      // Use requestAnimationFrame for reliable scroll after DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo(0, params.pageScrollY);
      });
    }
  }

  /**
   * Complete the restoration process
   * Clear flag and setup double-state, but DON'T pop (entry stays)
   */
  static completeRestoration() {
    CacheManager.setRestoreFlag(false);
    this.setupHistoryState();
    this.navigationInProgress = false;
  }

  /**
   * Cancel restoration (e.g., on error)
   */
  static cancelRestoration() {
    CacheManager.setRestoreFlag(false);
    this.navigationInProgress = false;
    this.setupHistoryState();
  }

  /**
   * Clear all history (for testing/debugging)
   */
  static clearHistory() {
    CacheManager.setHistoryStack([]);
    CacheManager.setRestoreFlag(false);
    this.navigationInProgress = false;
    if (this.scrollUpdateTimeout) {
      clearTimeout(this.scrollUpdateTimeout);
      this.scrollUpdateTimeout = null;
    }
  }

  /**
   * Get current history stack (for debugging)
   * @returns {Array}
   */
  static getStack() {
    return CacheManager.getHistoryStack();
  }

  /**
   * Debug: Log current state
   */
  static debugState() {
    // Debug method - no logging
  }
}

// ─────────────────────────────────────────────────────────────
// AUTO-INITIALIZE ON ALL PAGES
// ─────────────────────────────────────────────────────────────
document.addEventListener("DOMContentLoaded", () => {
  HistoryManager.initialize();

  // Check for ANY grid with data-infinite-scroll-v2 (not just #product-grid)
  // GridManager handles push/restore for collection pages
  const isCollectionPage = document.querySelector(
    "[data-infinite-scroll-v2]"
  );

  if (!isCollectionPage) {
    // Non-collection pages: HistoryManager handles everything
    if (HistoryManager.isRestoration()) {
      // Back navigation - restore scroll position
      HistoryManager.restoreScrollPosition();
      HistoryManager.completeRestoration();
    } else {
      // Fresh visit - push to stack
      const wasPushed = HistoryManager.pushCurrentPage();
      // If skipped (same URL on reload), skip pushState to avoid duplicate browser history entry
      HistoryManager.setupHistoryState(!wasPushed);
    }

    // Setup scroll listener for non-collection pages
    HistoryManager.setupScrollListener();
  }
  // Collection pages: InfiniteScrollController handles push/restore/scroll
});

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = HistoryManager;
}
